import axios from 'axios';
import { baseUrl } from '../config/environment';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: baseUrl,
  timeout: 50000, // 50 seconds
  headers: {
    'Content-Type': 'application/json',
    'Accept': '*/*',
    'Accept-Language': 'en-US,en;q=0.9',
  },
});

// Add request interceptor for retry logic
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Retry logic for cancelled/timeout errors
    if (error.code === 'ECONNABORTED' || error.message.includes('cancelled')) {
      if (!config._retry) {
        config._retry = true;
        config._retryCount = (config._retryCount || 0) + 1;
        
        if (config._retryCount <= 3) {
          console.log(`Retrying request (attempt ${config._retryCount}):`, config.url);
          await new Promise(resolve => setTimeout(resolve, 1000 * config._retryCount));
          return apiClient(config);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && error.config.url !== API_ENDPOINTS.LOGIN) {
      localStorage.removeItem('authToken');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const API_ENDPOINTS = {
  removeFromMyCars:'/removeFromMyCars',
  sendopt:'/sendOTP',
  verifyOTP:'/verifyOTP',
  downloadAgreement:'/downloadAgreement',
  generateAgreement:'/generateAgreement',
  dealerContract:'/dealerContract',
  uploadDocuments:'/uploadDocuments',
  RazerPayInit:'/RazerPayInit',
  RazerPayTranse:'RazerPayTranse',
  AmountPayGet:'/AmountPayGet',
  createDealerInfo:'/createDealerInfo',
  LOGIN_DEALER: '/loginDealer',
REGISTER_DEALER: '/registerDealer',
  INITIATE_DEALER_SIGNUP: '/initiateDealerSignup',
  VERIFY_DEALER_SIGNUP: '/verifyDealerSignup',
  DEALER_ONBOARDING_DETAILS: '/dealerOnboardingDetails',
  DEALER_ONBOARDING_CITIES: '/dealerOnboardingGetCities',
  DEALER_ONBOARDING_PAYMENT: '/dealerOnboardingPayment',
  DEALER_ONBOARDING_UPLOAD_DOCUMENTS: '/dealerOnboardingUploadDocuments',
  VALIDATE_DEALER_DOCUMENTS: '/validateDealerDocuments',

  LOGIN: '/login',
  LOGOUT: '/logout',
  VERIFY_SESSION: '/verifySession',
  GET_DEALER_HOME_DATA: '/getDealerHomeData',
  GET_DEALER_CARS_BY_CITY: '/getDealerCarsByCity',
  ADD_TO_MY_CARS: '/addToMyCars',
  CREATE_EVA_OTP:'/createOTPLoginAccess',
  GET_MY_CARS_BY_YARD: '/getMyCarsByYard',
  GET_CARS: '/getCars',
  GET_DEALER_PLACE_BIDS_CARS: '/getDealerPlaceBidsCars',
  GET_DEALER_DELIVERY_CARS: '/getDealerDeliveryCars',
  GET_DEALER_NOC_CARS: '/getDealerNOCCars',
  GET_DRIVERS: '/getDrivers',
  CREATE_DRIVER: '/createDriver',
  CREATE_GATEPASS: '/createGatepass',
  CREATE_DELIVERY_GATEPASS: '/createDeliveryGatepass',
  GET_DEALER_EVALUATION_CARS: '/getDealerEvaluationCars',
  GET_EVALUATOR_CARS: '/getEvaluatorCars',
  GET_DRIVER_CARS: '/getDriverCars',
  SHARE_EVAL_LINK: '/shareEvalLink',
  UPDATE_CAR_WORKFLOW_STATUS: '/updateCarWorkflowStatus',
  GET_CAR_DETAILS: '/getCarDetails',
  GET_CAR_DETAILS_PAGE_DATA: '/getCarDetailsPageData',
  GET_CAR_DETAILS_BY_FLOW_ENTITY: '/getCarDetailsByFlowEntity',
  UPLOAD_FILE: '/uploadFile',
  SEND_CHAT_MESSAGE: '/sendChatMessage',
  UPDATE_DELIVERY_PHOTOS: '/updateDeliveryPhotos',
  UPDATE_CAR_EVALUATION: '/updateCarEvaluation',
  SUBMIT_EVALUATION: '/submitEvaluation',
  PLACE_BID: '/placeBidForCar',
  GET_DEALER_PAYMENT_CARS: '/getDealerPaymentCars',
  SUBMIT_PAYMENT_INFO: '/submitPaymentInfo',
  GENERATE_DELIVERY_LINK: '/generateDeliveryLink',
  CONFIRM_DELIVERY: '/confirmDelivery',
  // NOC Functions
  SUBMIT_KYC: '/submitKYC',
  ACKNOWLEDGE_NOC_RECEIPT: '/acknowledgeNOCReceipt',
  SUBMIT_NEW_RC: '/submitNewRC',
  GET_DOCUMENT_HISTORY: '/getDocumentHistory',
  GET_PUBLIC_FEATURED_CARS: '/getPublicFeaturedCars',
};

// API service methods
export const apiService = {
  removeFromMyCars: (params) => {
    return apiClient.post(API_ENDPOINTS.removeFromMyCars,params);


  },


  sendOTP: (params) => {
    return apiClient.post(API_ENDPOINTS.sendopt,params);
  },
 verifyOTP: (params) => {
    return apiClient.post(API_ENDPOINTS.verifyOTP,params);
  },
  downloadAgreement: (params) => {
    return apiClient.post(API_ENDPOINTS.downloadAgreement,params);
  },
  generateAgreement: (params) => {
    return apiClient.post(API_ENDPOINTS.generateAgreement,params);
  },
  dealerContract: (params) => {
    return apiClient.post(API_ENDPOINTS.dealerContract,params);
  },
  uploadDocuments: (params) => {
    return apiClient.post(API_ENDPOINTS.uploadDocuments,params);
  },
  RazerPayInit: (params) => {
    return apiClient.post(API_ENDPOINTS.RazerPayInit,params);
  },
  RazerPayTranse: (params) => {
    return apiClient.post(API_ENDPOINTS.RazerPayTranse,params);
  },

  AmountPayGet: (params) => {
    return apiClient.post(API_ENDPOINTS.AmountPayGet,params);
  },

  createDealerInfo: (data) => {
    return apiClient.post(API_ENDPOINTS.createDealerInfo, data);
  },

  loginDealer: (credentials) => {
    return apiClient.post(API_ENDPOINTS.LOGIN_DEALER, credentials);
  },

  registerDealer: (data) => {
    return apiClient.post(API_ENDPOINTS.REGISTER_DEALER, data);
  },

  initiateDealerSignup: (data) => {
    return apiClient.post(API_ENDPOINTS.INITIATE_DEALER_SIGNUP, data);
  },

  verifyDealerSignup: (data) => {
    return apiClient.post(API_ENDPOINTS.VERIFY_DEALER_SIGNUP, data);
  },

  dealerOnboardingDetails: (data) => {
    return apiClient.post(API_ENDPOINTS.DEALER_ONBOARDING_DETAILS, data);
  },

  validateDealerDocuments: (data) => {
    return apiClient.post(API_ENDPOINTS.VALIDATE_DEALER_DOCUMENTS, data);
  },

  dealerOnboardingGetCities: () => {
    return apiClient.get(API_ENDPOINTS.DEALER_ONBOARDING_CITIES);
  },

  dealerOnboardingPayment: (data) => {
    return apiClient.post(API_ENDPOINTS.DEALER_ONBOARDING_PAYMENT, data);
  },

  dealerOnboardingUploadDocuments: (data) => {
    return apiClient.post(API_ENDPOINTS.DEALER_ONBOARDING_UPLOAD_DOCUMENTS, data);
  },

  get: async (endpoint, params = {}) => {
    const res = await apiClient.get(endpoint, { params });
    return res;
  },
  post: async (endpoint, data = {}) => {
    const res = await apiClient.post(endpoint, data);
    return res;
  },
  put: async (endpoint, data = {}) => {
    const res = await apiClient.put(endpoint, data);
    return res;
  },
  delete: async (endpoint) => {
    const res = await apiClient.delete(endpoint);
    return res;
  },
  logout: async () => {
    const res = await apiClient.post(API_ENDPOINTS.LOGOUT, {});
    return res;
  },
  login: (credentials) => {
    return apiClient.post(API_ENDPOINTS.LOGIN, credentials);
  },
  verifySession: async () => {
    const res = await apiClient.get(API_ENDPOINTS.VERIFY_SESSION);
    return res;
  },
  getDealerHomeData: (orgId, dealerId) => {
    return apiClient.get(API_ENDPOINTS.GET_DEALER_HOME_DATA, {
      params: { orgId: `${orgId}&dealerId=${dealerId}` }
    });
  },
  getDealerCarsByCity: (orgId, dealerId, city) => {
    return apiClient.get(API_ENDPOINTS.GET_DEALER_CARS_BY_CITY, {
      params: { 
        orgId: `${orgId}&dealerId=${dealerId}`,
        city 
      }
    });
  },
  addToMyCars: (data) => {
    return apiClient.post(API_ENDPOINTS.ADD_TO_MY_CARS, data);
  },
 createOTPLoginAccess:(data) => {
    return apiClient.post(API_ENDPOINTS.CREATE_EVA_OTP, data);
  },
  
  getMyCarsByYard: (params) => {
    return apiClient.get(API_ENDPOINTS.GET_MY_CARS_BY_YARD, { params });
  },
  getCars: (params) => {
    return apiClient.get(API_ENDPOINTS.GET_CARS, { params });
  },
  getDealerPlaceBidsCars: (params) => {
    return apiClient.get(API_ENDPOINTS.GET_DEALER_PLACE_BIDS_CARS, { params });
  },
  getDealerDeliveryCars: () => {
    return apiClient.get(API_ENDPOINTS.GET_DEALER_DELIVERY_CARS);
  },
  getDealerNOCCars: () => {
    return apiClient.get(API_ENDPOINTS.GET_DEALER_NOC_CARS);
  },
  getDrivers: (type = null) => {
    const url = type ? `${API_ENDPOINTS.GET_DRIVERS}?type=${encodeURIComponent(type)}` : API_ENDPOINTS.GET_DRIVERS;
    return apiClient.get(url);
  },
  createDriver: (driverData) => {
    return apiClient.post(API_ENDPOINTS.CREATE_DRIVER, driverData);
  },
  createGatepass: async (gatePassData) => {
    const res = await apiClient.post(API_ENDPOINTS.CREATE_GATEPASS, gatePassData);
    return res;
  },
  createDeliveryGatepass: async (gatePassData) => {
    console.log("createDeliveryGatepass: Sending request with payload:", JSON.stringify(gatePassData, null, 2));
    const res = await apiClient.post(API_ENDPOINTS.CREATE_DELIVERY_GATEPASS, gatePassData);
    console.log("createDeliveryGatepass: Response:", JSON.stringify(res.data, null, 2));
    return res;
  },
  shareEvalLink: async (carIds) => {
    const res = await apiClient.post(API_ENDPOINTS.SHARE_EVAL_LINK, { carIds });
    return res;
  },
  updateCarWorkflowStatus: async (data) => {
    const res = await apiClient.post(API_ENDPOINTS.UPDATE_CAR_WORKFLOW_STATUS, data);
    return res.data;
  },
  getCarDetails: async (carId) => {
    const res = await apiClient.get(API_ENDPOINTS.GET_CAR_DETAILS, { params: { carId } });
    return res;
  },
  getCarDetailsPageData: async (carId) => {
    const res = await apiClient.get(API_ENDPOINTS.GET_CAR_DETAILS_PAGE_DATA, { params: { carId } });
    return res;
  },
  getCarDetailsByFlowEntity: async (flowEntityId) => {
    console.log('apiService.getCarDetailsByFlowEntity called for flowEntityId:', flowEntityId);
    const res = await apiClient.get(API_ENDPOINTS.GET_CAR_DETAILS_BY_FLOW_ENTITY, {
      params: { flowEntityId }
    });
    return res;
  },
  uploadFile: async (fileData) => {
    const base64String = fileData.file.includes('base64,')
      ? fileData.file.split('base64,')[1]
      : fileData.file;
    const updatedFileData = { ...fileData, file: base64String };
    
    console.log("uploadFile: Sending request with payload:", JSON.stringify(updatedFileData, null, 2));
    const res = await apiClient.post(API_ENDPOINTS.UPLOAD_FILE, updatedFileData, {
      headers: { 
        'Content-Type': 'application/json',
        'Accept': '*/*',
        'Accept-Language': 'en-US,en;q=0.9',
      },
    });
    console.log("uploadFile: Response:", JSON.stringify(res.data, null, 2));
    return res;
  },
  updateDeliveryPhotos: async (data) => {
    console.log("updateDeliveryPhotos: Sending request with payload:", JSON.stringify(data, null, 2));
    const res = await apiClient.post(API_ENDPOINTS.UPDATE_DELIVERY_PHOTOS, data, {
      headers: { 
        'Content-Type': 'application/json',
        'Accept': '*/*',
        'Accept-Language': 'en-US,en;q=0.9',
      },
    });
    console.log("updateDeliveryPhotos: Response:", JSON.stringify(res.data, null, 2));
    return res;
  },
  updateCarEvaluation: async (data) => {
    const res = await apiClient.post(API_ENDPOINTS.UPDATE_CAR_EVALUATION, data);
    return res.data;
  },
  submitEvaluation: async (data) => {
    const res = await apiClient.post(API_ENDPOINTS.SUBMIT_EVALUATION, data);
    return res.data;
  },
  placeBid: async (data) => {
    const res = await apiClient.post(API_ENDPOINTS.PLACE_BID, data);
    return res.data;
  },
  getDealerPaymentCars: () => {
    return apiClient.get(API_ENDPOINTS.GET_DEALER_PAYMENT_CARS);
  },
  submitPaymentInfo: async (paymentData) => {
    const res = await apiClient.post(API_ENDPOINTS.SUBMIT_PAYMENT_INFO, paymentData, {
      headers: { 
        'Content-Type': 'application/json',
      },
    });
    return res.data;
  },
  getDealerEvaluationCars: async () => {
    console.log('apiService.getDealerEvaluationCars called');
    const res = await apiClient.get(API_ENDPOINTS.GET_DEALER_EVALUATION_CARS);
    return res;
  },
  getEvaluatorCars: async () => {
    console.log('apiService.getEvaluatorCars called');
    const res = await apiClient.get(API_ENDPOINTS.GET_EVALUATOR_CARS);
    return res;
  },
    getDriverCars: async () => {
    console.log('apiService.getDriverCars called');
    const res = await apiClient.get(API_ENDPOINTS.GET_DRIVER_CARS);
    return res;
  },
  generateDeliveryLink: async (data) => {
    console.log("generateDeliveryLink: Sending request with payload:", JSON.stringify(data, null, 2));
    const res = await apiClient.post(API_ENDPOINTS.GENERATE_DELIVERY_LINK, data);
    console.log("generateDeliveryLink: Response:", JSON.stringify(res.data, null, 2));
    return res;
  },
  confirmDelivery: async (data) => {
    console.log("confirmDelivery: Sending request with payload:", JSON.stringify(data, null, 2));
    const res = await apiClient.post(API_ENDPOINTS.CONFIRM_DELIVERY, data);
    console.log("confirmDelivery: Response:", JSON.stringify(res.data, null, 2));
    return res;
  },
  // NOC Functions
  submitKYC: async (data) => {
    console.log("submitKYC: Sending request with payload:", JSON.stringify(data, null, 2));
    const res = await apiClient.post(API_ENDPOINTS.SUBMIT_KYC, data);
    console.log("submitKYC: Response:", JSON.stringify(res.data, null, 2));
    return res;
  },
  acknowledgeNOCReceipt: async (data) => {
    console.log("acknowledgeNOCReceipt: Sending request with payload:", JSON.stringify(data, null, 2));
    const res = await apiClient.post(API_ENDPOINTS.ACKNOWLEDGE_NOC_RECEIPT, data);
    console.log("acknowledgeNOCReceipt: Response:", JSON.stringify(res.data, null, 2));
    return res;
  },
  submitNewRC: async (data) => {
    console.log("submitNewRC: Sending request with payload:", JSON.stringify(data, null, 2));
    const res = await apiClient.post(API_ENDPOINTS.SUBMIT_NEW_RC, data);
    console.log("submitNewRC: Response:", JSON.stringify(res.data, null, 2));
    return res;
  },
  getDocumentHistory: async (carId) => {
    console.log("getDocumentHistory: Getting history for car:", carId);
    const res = await apiClient.get(API_ENDPOINTS.GET_DOCUMENT_HISTORY, {
      params: { carId }
    });
    console.log("getDocumentHistory: Response:", JSON.stringify(res.data, null, 2));
    return res;
  },
  sendChatMessage: async (data) => {
    console.log("sendChatMessage: Sending message:", JSON.stringify(data, null, 2));
    const res = await apiClient.post(API_ENDPOINTS.SEND_CHAT_MESSAGE, data);
    console.log("sendChatMessage: Response:", JSON.stringify(res.data, null, 2));
    return res;
  },
  getPublicFeaturedCars: async () => {
    const res = await apiClient.get(API_ENDPOINTS.GET_PUBLIC_FEATURED_CARS);
    return res;
  },
};

export default apiService;